import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import LazyLoad from "react-lazyload";
import useDiscountsInfo from "@hooks/useDiscountsInfo";
import { useCurrentCountryCode, usePriceLocalInfo } from "@hooks/usePrices";

import {
  getProductPreOrderShippingDate,
  isOutOfStock,
  isProductPreOrder,
} from "@helpers/product";
import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";

import { ComponentType } from "@constants/ComponentType";
import { HandleType } from "@constants/HandleType";

import ProductCollectionContent from "./ProductCollectionContent";
import PriceContainer from "@components/PriceContainer";
import ProductMention from "@components/ProductMention";
import { ALSkeleton } from "@components/ALComponents";

import "./productCollection.scss";

function ProductCollectionContentPlaceholder({ product, prices, componentType }) {
  const discountsInfo = useDiscountsInfo();
  const countryCode = useCurrentCountryCode();
  const contentfulProduct = product.contentful.node;
  const isMtoApp = componentType === ComponentType.MTO_APP;
  const materials = contentfulProduct.productDetails?.components;
  const material =
    isMtoApp && materials?.find((value) => ["Gold Vermeil", "10K Solid Gold"].includes(value));
  const customizationLabel = isMtoApp
    ? contentfulProduct.productDetails?.customizableType?.join(", ")
    : "";
  const subtitle = isMtoApp ? material : contentfulProduct.title.split(" - ")[0];
  const isSoldOut =
    isOutOfStock(product.node, countryCode) && product.node.handle !== HandleType.GIFT_CARD;

  const price = prices?.find((p) => p?.node?.country === countryCode)?.node;
  const productPrices = usePriceLocalInfo({
    handle: product.node.handle,
    price,
    tags: { [product.node.handle]: product.node.tags },
    shouldUseDiscount: true,
  });

  const productMentionData = useMemo(
    () => ({
      id: product.node.id,
      tags: product.node.tags,
      isPreOrder: isProductPreOrder(product),
      preOrderShippingDate: getProductPreOrderShippingDate(product),
      type: product.node.productType,
    }),
    [product]
  );

  return (
    <div className="product_collection product_collection_placeholder w-full h-full">
      <a href={`/products/${contentfulProduct.handle}`}>
        <div className="product_collection_placeholder__image_wrapper">
          <div className="product_collection_placeholder__image">
            <ALSkeleton />
          </div>
        </div>
        <div className="product_collection_details">
          {!isMtoApp && !isSoldOut && (
            <ProductMention
              prices={productPrices}
              product={productMentionData}
              discountsInfo={discountsInfo}
              globalProductMention={discountsInfo?.[0]?.globalProductText}
              componentType={componentType}
            />
          )}
          <div className="product_collection_details__wrapper">
            <div className="pr-[5px]">
              <h2 className="m-0 subtext subtext--bold subtext--uc black">
                {contentfulProduct.title.split(" - ")[1]}
              </h2>
              {subtitle && (
                <h3 className="product_collection_details_subtext microtext dark-grey">
                  {subtitle}
                </h3>
              )}
            </div>
            {!isSoldOut && (
              <PriceContainer
                componentType={componentType}
                productHandle={product.node.handle}
                tags={product.node.tags}
                prices={productPrices}
                pricesLoading={false}
                productId={product.node.shopifyId}
              />
            )}
          </div>
          {customizationLabel && (
            <p className="typo-micro italic mt-[10px]">{customizationLabel}</p>
          )}
        </div>
      </a>
      {!isMtoApp &&
        contentfulProduct?.productAlternatives?.alternatives.map((alternative) => {
          if (alternative?.handle === contentfulProduct?.handle) return null;
          return (
            <a
              className="microtext dark-grey"
              key={`${contentfulProduct?.handle}_alternative_${alternative.handle}`}
              href={`/products/${alternative?.handle}`}
            >
              {alternative.attributes.map((a) => {
                const attr = a.split("-");
                return `${attr[0].trim()}: ${attr[attr.length - 1].trim()} `;
              })}
            </a>
          );
        })}
    </div>
  );
}

function ProductCollection(props) {
  const {
    collectionHandle,
    product,
    componentType,
    orderProduct,
    shouldOrderProducts,
    prices,
    searchObject,
    onCustomize,
  } = props;
  const [selectedAlternativeHandle, setSelectedAlternativeHandle] = useState(product.node.handle);

  const activeAlternative =
    (product.contentful.node?.productAlternatives?.alternatives || []).find(
      (p) => p.node?.handle === selectedAlternativeHandle
    ) || product;

  const isLazyloadEnabled = componentType !== ComponentType.SEARCH;

  let variantTitleActiveAlternative = activeAlternative;
  if (searchObject?.isSearch) {
    variantTitleActiveAlternative = {
      ...activeAlternative,
      variant: product.variant,
      productPath: product.productPath,
      contentful: {
        ...activeAlternative.contentful,
        title: product.contentful.node.title,
        node: {
          ...activeAlternative.contentful.node,
          title: product.contentful.node.title,
        },
      },
    };
  }

  const handleProductClick = () => {
    const scrollPosition = window.scrollY;
    window.history.pushState(
      {
        ...window.history.state,
        scrollPosition,
      },
      document.title
    );
  };

  useEffect(() => {
    if (componentType === ComponentType.MTO_APP) return;

    const scrollPosition = window.history.state?.scrollPosition || 0;
    window.scrollTo(0, scrollPosition);
  }, [componentType]);

  return (
    <div
      className={`pc_${product?.node?.handle} ${
        shouldOrderProducts ? `order-${orderProduct} ${collectionHandle}-order-${orderProduct}` : ""
      } product_collection-${componentType?.toLowerCase()}-wrapper relative w-full`}
      onClick={handleProductClick}
    >
      {isLazyloadEnabled ? (
        <LazyLoad
          once
          placeholder={
            <ProductCollectionContentPlaceholder
              product={variantTitleActiveAlternative}
              prices={prices}
              componentType={componentType}
            />
          }
        >
          <ProductCollectionContent
            {...{
              ...props,
              onCustomize,
              product: variantTitleActiveAlternative,
              setActiveAlternative: setSelectedAlternativeHandle,
            }}
          />
        </LazyLoad>
      ) : (
        <ProductCollectionContent
          {...{
            ...props,
            onCustomize,
            product: variantTitleActiveAlternative,
            setActiveAlternative: setSelectedAlternativeHandle,
          }}
        />
      )}
    </div>
  );
}

ProductCollection.propTypes = {
  collectionHandle: PropTypes.string,
  product: PropTypes.shape({
    contentful: PropTypes.shape({
      node: PropTypes.shape({
        handle: PropTypes.string.isRequired,
        productAlternatives: PropTypes.shape({
          alternatives: PropTypes.array,
        }),
      }).isRequired,
    }),
    node: PropTypes.shape({
      id: PropTypes.string.isRequired,
      handle: PropTypes.string.isRequired,
      shopifyId: PropTypes.string.isRequired,
      productType: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string).isRequired,
      variants: PropTypes.arrayOf(
        PropTypes.shape({
          availableForSale: PropTypes.bool.isRequired,
          inventoryPolicy: PropTypes.string.isRequired,
        })
      ).isRequired,
    }).isRequired,
  }),
  componentType: PropTypes.string.isRequired,
  orderProduct: PropTypes.number,
  shouldOrderProducts: PropTypes.bool,
  searchObject: PropTypes.shape({
    isSearch: PropTypes.bool,
    index: PropTypes.string,
    queryID: PropTypes.string,
    objectID: PropTypes.string,
  }),
  prices: PropTypes.array,
  onCustomize: PropTypes.func,
};

export default withALErrorBoundary({
  name: "ProductCollection",
  priority: "P2",
})(ProductCollection);
